/**
 * HTTPリクエストを行うためのUtilです。
 */

// eslint-disable-next-line max-classes-per-file
import qs from 'qs';
import $ from 'jquery';
import Ajv from 'ajv';
import { AxiosError } from 'axios';
import { serialize } from 'object-to-formdata';
import { reportError } from '@this/lib/bugsnag';
import Notification from '../../notification';
import { createHttpClient } from './http_client_builder';

/**
 * memo: jqueryによる特殊なparseでないとRails側で受けれないケースがあるため、parserでjqueryを指定できるようにする。
 * 特殊性はunit testを参照
 */
export const toParams = (
  obj: any,
  options: qs.IStringifyOptions & { parser?: 'qs' | 'jquery' } = { arrayFormat: 'brackets', parser: 'qs' }
) => (options.parser === 'jquery' ? $.param(obj) : qs.stringify(obj, options));

export const getParams = () => {
  if (!(window.location && window.location.search)) {
    return {};
  }

  return qs.parse(window.location.search.substring(1)) as unknown as Record<string, any>;
};

export const getParam = (key: string) => {
  return getParams()[key];
};

export const objectToFormData = (
  obj: any,
  options: { parser?: 'object-to-formdata' | 'jquery' } = { parser: 'object-to-formdata' }
) => {
  if (options.parser === 'jquery') {
    const paramString = toParams(obj, { parser: 'jquery' });
    const searchParams = new URLSearchParams(paramString);
    const formData = new FormData();
    searchParams.forEach((value, key) => formData.append(key, value));

    return formData;
  }

  return serialize(obj, { indices: true });
};

const ipAddressErrorKey = 'unallowed_ip_address';

/**
 * HTTPリクエスト例外を表すカスタムエラー。
 */
export class HTTPError extends Error {
  constructor(
    message: string,
    public request: { url: string; data?: Record<string, any> },
    public response?: { data: Record<string, any>; status: number }
  ) {
    super(message);

    // エラートラッキンツール上でエラーの概要がわかるように。クエリパラメータは除去し、id部分はグルーピングしやすくするため丸める
    this.name = `${response?.status ?? 'ステータス不明'}(${request.url
      .split('?')[0]
      .replace(/\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/gi, '{id}')
      .replace(/\d+/g, '{id}')})`;
  }
}

/**
 * HTTPリクエストをするためのクラス。
 *
 * @example
 * HTTPメソッドに対応する関数を呼ぶことでリクエストが可能です
 *
 * ```
 * try {
 *   const res = await await Fetcher.get('/get')<{res: any}>
 *   console.log(res) // Prints response data
 * } catch(e) {
 * 　if(e instanceof HttpError) {
 *     console.log(e.response?.status) // status code
 *     console.log(e.response?.data) // error response data
 *   }
 * }
 * ```
 *
 */
export class Fetcher {
  static client = createHttpClient();

  static async get<T>(
    url: string,
    data?: Record<string, any>,
    dataOptions?: Parameters<typeof toParams>[1],
    signal?: AbortSignal
  ) {
    const requestUrl = data ? `${url}?${toParams(data, dataOptions)}` : url;
    return this.request<T>({ url: requestUrl, method: 'GET', signal });
  }

  static async post<T>(url: string, data: Record<string, any>, signal?: AbortSignal) {
    return this.request<T>({ url, method: 'POST', data, signal });
  }

  static async put<T>(url: string, data: Record<string, any>, signal?: AbortSignal) {
    return this.request<T>({ url, method: 'PUT', data, signal });
  }

  static async patch<T>(url: string, data?: Record<string, unknown>, signal?: AbortSignal) {
    return this.request<T>({ url, method: 'PATCH', data, signal });
  }

  static async delete<T>(
    url: string,
    data?: Record<string, any>,
    dataOptions?: Parameters<typeof toParams>[1],
    signal?: AbortSignal
  ) {
    const requestUrl = data ? `${url}?${toParams(data, dataOptions)}` : url;
    return this.request<T>({ url: requestUrl, method: 'DELETE', signal });
  }

  static async upload<T>(
    url: string,
    data: FormData,
    { method }: { method: 'POST' | 'PUT' | 'PATCH' } = { method: 'POST' },
    signal?: AbortSignal
  ) {
    return this.request<T>({ url, method, data, contentType: 'multipart/form-data', signal });
  }

  static async request<T>({
    url,
    method,
    data,
    contentType = 'application/json',
    signal
  }: {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: Record<string, any>;
    contentType?: string;
    signal?: AbortSignal;
  }) {
    try {
      // NOTE: 内部的にはaxiosを使用。Fetcherクラスで隠蔽することで将来的にaxios以外に変更が可能にしています。
      const res = await Fetcher.client(url, {
        method,
        data,
        signal,
        headers: {
          'Content-Type': contentType
        }
      });

      return res.data as T;
    } catch (e) {
      // AbortErrorまたはAxiosのキャンセルエラーの場合はそのまま再スロー
      if (e instanceof Error && e.name === 'AbortError') {
        throw e;
      }

      // Axiosのキャンセルエラーの場合もAbortErrorとして扱う
      if (e instanceof AxiosError && (e.code === 'ERR_CANCELED' || e.message === 'canceled')) {
        const abortError = new Error('The operation was aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }

      const response =
        e instanceof AxiosError && e.response ? { data: e.response.data, status: e.response.status } : undefined;
      const message =
        response?.data?.errors?.[0] ??
        response?.data?.[ipAddressErrorKey] ??
        JSON.stringify(response?.data) ??
        e.message ??
        'HTTPリクエストでエラーが発生しました。';

      const error = new HTTPError(message, { url, data }, response);

      // IPアドレス制限
      if (error.response?.data?.[ipAddressErrorKey]) {
        Notification.error(error.response.data[ipAddressErrorKey]);

        // IPアドレス制限かかるとセッション切れるのでリロードさせて、強制的にログイン画面に戻す。
        window.location.reload();
      }

      reportError(error);
      throw error;
    }
  }
}

/**
 * JSON Schema検証付きFetcher（開発環境のみ）
 * 詳細は js/scripts/schema/README.md を参照
 */
export class StrictFetcher {
  // スキーマのキャッシュ（同じスキーマを何度も取得しないようにする）
  private static schemaCache: Map<string, any> = new Map();

  static async get<T>(
    url: string,
    schemaPath: string,
    data?: Record<string, any>,
    dataOptions?: Parameters<typeof toParams>[1],
    signal?: AbortSignal
  ): Promise<T> {
    const response = await Fetcher.get<T>(url, data, dataOptions, signal);
    return this.validate<T>(url, schemaPath, data, response);
  }

  static async post<T>(
    url: string,
    schemaPath: string,
    data: Record<string, any>,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await Fetcher.post<T>(url, data, signal);
    return this.validate<T>(url, schemaPath, data, response);
  }

  static async put<T>(
    url: string,
    schemaPath: string,
    data: Record<string, any>,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await Fetcher.put<T>(url, data, signal);
    return this.validate<T>(url, schemaPath, data, response);
  }

  static async delete<T>(
    url: string,
    schemaPath: string,
    data?: Record<string, any>,
    dataOptions?: Parameters<typeof toParams>[1],
    signal?: AbortSignal
  ): Promise<T> {
    const response = await Fetcher.delete<T>(url, data, dataOptions, signal);
    return this.validate<T>(url, schemaPath, data, response);
  }

  private static async validate<T>(
    url: string,
    schemaPath: string,
    data: Record<string, any> | undefined,
    response: any
  ): Promise<T> {
    if (process.env.NODE_ENV === 'production') {
      return response;
    }

    try {
      // キャッシュから取得、なければ取得してキャッシュに保存
      let schema = this.schemaCache.get(schemaPath);
      if (!schema) {
        schema = await Fetcher.get<any>(`/schema/${schemaPath}`);
        this.schemaCache.set(schemaPath, schema);
      }

      this.validateAndShowError(url, schema, response);
    } catch (e) {
      if (e instanceof HTTPError) {
        throw e;
      }
      throw new HTTPError(e.message, { url, data }, response);
    }

    return response;
  }

  private static validateAndShowError(url: string, schema: any, response: any): void {
    const ajv = new Ajv({ allErrors: true });
    const validate = ajv.compile(schema);
    const valid = validate(response);
    if (valid) {
      // eslint-disable-next-line no-console
      console.log(`${url}: valid`);
      return;
    }

    // eslint-disable-next-line no-console
    console.error(JSON.stringify(validate.errors, null, 2));
    // eslint-disable-next-line no-console
    console.error(
      `${url}\nJSON Schema検証エラーが発生しました。\n\n${ajv.errorsText(validate.errors, { separator: '\n' })}`
    );
    // eslint-disable-next-line no-console
    console.error('JSON Schema validationについて: js/scripts/schema/README.md');
    // eslint-disable-next-line no-alert
    // alert(`${url}\nJSON Schema検証エラーが発生しました。\n\ndevtoolコンソールで詳細を確認してください。`);
  }
}
